import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styles from '../MemberFormModal/MemberFormModal.module.css';

import { participationStatusOptions } from 'config/admin/clubMembers';

import {
  addSecondaryContact,
  updateSecondaryContact,
  findNameMatches,
  lookupSecondaryCandidateByEmail,
} from 'api/userApi';

import NameInputBox from 'components/common/NameInputBox';
import CustomInput from 'components/common/CustomInput/CustomInput';
import CustomSelect from 'components/common/CustomSelect';
import CustomDropdown from 'components/NewDesign/CustomDropdown/CustomDropdown';
import LabelText from 'components/common/LabelText';
import UserLookupConfirmDialog from 'components/admin/UserLookupConfirmDialog';
import MemberSearchPicker from 'components/admin/MemberSearchPicker/MemberSearchPicker';
import { roleOptionsFor } from '../MemberFormModal/MemberFormModal';
import { getUserPermissionLevel } from 'services/auth/tokenService';
import { UserPermissionLevel } from 'constants/auth';

// Whether a club member/guest can be attached as this family's secondary contact. The directory rows carry
// FK-derived ContactRole/LinkedPrimaryName, so we can block ineligible people (and say why) before submit;
// the server re-validates as the backstop. See docs/secondary-contact-decisions.md.
const secondaryEligibility = (p) => {
  if (p.contactRole === 'PrimaryContact') {
    return { eligible: false, reason: 'already a primary contact for athletes' };
  }
  if (p.contactRole === 'SecondaryContact') {
    return {
      eligible: false,
      reason: p.linkedPrimaryName
        ? `already secondary for ${p.linkedPrimaryName}`
        : 'already a secondary contact for another family',
    };
  }
  return { eligible: true };
};

const SecondaryContactModal = ({
  show,
  contact = null,
  primaryContactId,
  primaryContactName,
  clubId,
  onClose,
  onSuccess,
}) => {
  const isEdit = !!contact;

  // Role and Club Status are Admin/Owner-only to edit (the primary may edit the name, but not these). At creation
  // the server sets the role to match the family — Member, or Guest for a guest family. See docs/secondary-contact-decisions.md.
  const canEditRoleAndStatus =
    getUserPermissionLevel() >= UserPermissionLevel.Admin;

  // Email-edit policy (Phase 1): an admin may always change the email; the family primary may change it only until the
  // secondary has accepted/validated it (invitationStatus !== 'Accepted'). The server re-enforces this. See
  // docs/secondary-contact-family-ui-plan.html §16.
  const canEditEmail =
    canEditRoleAndStatus || contact?.invitationStatus !== 'Accepted';

  // Edit-mode form state.
  const [formValues, setFormValues] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'Member',
    participationStatus: 'Active',
  });
  const [errors, setErrors] = useState({});

  // Add-mode state: the search/create picker reports its current choice here.
  const [selection, setSelection] = useState(null);

  const [loading, setLoading] = useState(false);

  // Existing-account preflight (create path): the typed email may already belong to an account.
  const [lookupResult, setLookupResult] = useState(null);
  const [showLookupDialog, setShowLookupDialog] = useState(false);

  useEffect(() => {
    if (isEdit && contact) {
      setFormValues({
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        email: contact.email || '',
        role: contact.role || 'Member',
        participationStatus: contact.participationStatus || 'Active',
      });
    } else {
      setFormValues({
        firstName: '',
        lastName: '',
        email: '',
        role: 'Member',
        participationStatus: 'Active',
      });
    }
    setErrors({});
    setSelection(null);
    setLookupResult(null);
    setShowLookupDialog(false);
  }, [isEdit, contact, show]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleDropBoxChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  // Edit-mode validation (name + status).
  const validateEditForm = () => {
    const newErrors = {};
    if (!formValues.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formValues.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formValues.participationStatus) {
      newErrors.participationStatus = 'Status is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Server-side validations (defence-in-depth) still produce errors that we should surface verbatim.
  const surfaceServerError = (error, fallback) => {
    console.error('Secondary contact form error:', error);
    const msg =
      error?.response?.data?.message ||
      error?.response?.data ||
      error?.message ||
      fallback;
    toast.error(typeof msg === 'string' ? msg : fallback);
  };

  // --- Edit flow ---
  const submitEdit = async () => {
    setLoading(true);
    try {
      const contactData = {
        userID: contact.userID,
        firstName: formValues.firstName.trim(),
        lastName: formValues.lastName.trim(),
        email: formValues.email.trim(),
        // Role/status are only sent by an Admin/Owner; the server also enforces this. The primary editing the
        // name omits them so there is no accidental (and server-rejected) role/status change.
        ...(canEditRoleAndStatus
          ? {
              role: formValues.role,
              participationStatus: formValues.participationStatus,
            }
          : {}),
      };
      await updateSecondaryContact(clubId, primaryContactId, contactData);
      toast.success('Secondary contact updated successfully');
      onSuccess();
    } catch (error) {
      surfaceServerError(error, 'Failed to update secondary contact');
    } finally {
      setLoading(false);
    }
  };

  // --- Add flow ---
  // Attaches an EXISTING account by id (selected from the directory, surfaced via the masked heads-up, or
  // confirmed via the email preflight — the client may never have had its full email), OR creates a new person
  // by email. The server enforces the one-family rule and attaches-vs-creates. See docs/secondary-contact-decisions.md.
  const addContact = async ({ secondaryUserId, email, firstName, lastName, createUserIfNotExists }) => {
    setLoading(true);
    try {
      await addSecondaryContact(clubId, primaryContactId, {
        secondaryUserId: secondaryUserId || undefined,
        email: (email || '').trim(),
        firstName: (firstName || '').trim(),
        lastName: (lastName || '').trim(),
        createUserIfNotExists,
      });
      toast.success('Secondary contact added successfully');
      setShowLookupDialog(false);
      setLookupResult(null);
      onSuccess();
    } catch (error) {
      surfaceServerError(error, 'Failed to add secondary contact');
    } finally {
      setLoading(false);
    }
  };

  const submitAdd = async () => {
    if (!selection || !selection.valid) return;

    if (selection.type === 'existing') {
      // Deliberate pick of a labeled, existing account → attach by id. The duplicate heads-up already had its say.
      await addContact({
        secondaryUserId: selection.person.userID,
        createUserIfNotExists: false,
      });
      return;
    }

    // New person: preflight by email — it may already belong to an account, in which case we confirm/attach
    // rather than create a duplicate (this is the core "did you know this user exists?" detection).
    setLoading(true);
    try {
      const response = await lookupSecondaryCandidateByEmail(
        clubId,
        primaryContactId,
        selection.email.trim()
      );
      if (response.data?.exists) {
        setLookupResult(response.data);
        setShowLookupDialog(true);
        setLoading(false);
        return;
      }
      // No matching account — create the new user.
      await addContact({
        email: selection.email,
        firstName: selection.firstName,
        lastName: selection.lastName,
        createUserIfNotExists: true,
      });
    } catch (error) {
      surfaceServerError(error, 'Failed to look up user by email');
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (isEdit) {
      if (!validateEditForm()) return;
      await submitEdit();
      return;
    }
    await submitAdd();
  };

  // Confirmed attaching the existing account that matched the typed email — attach by id, with the typed email, which
  // the server requires for someone from outside the club (Doug, 2026-11-05).
  const handleLookupConfirm = async () => {
    await addContact({
      secondaryUserId: lookupResult?.userID,
      email: selection?.email,
      createUserIfNotExists: false,
    });
  };

  const handleLookupCancel = () => {
    setShowLookupDialog(false);
    setLookupResult(null);
    // Stay on the form so the admin can edit the email or pick someone else.
  };

  // Duplicate detection for the picker — scoped to this family for authorization. Surfaces other people in this club
  // who share the name; never anyone outside it (Doug, 2026-11-05). Emails are masked server-side per the viewer.
  const findDuplicates = useCallback(
    async ({ firstName, lastName, excludeUserId }) => {
      try {
        const resp = await findNameMatches(clubId, primaryContactId, firstName, lastName, excludeUserId);
        return resp?.data?.matches || [];
      } catch (e) {
        return [];
      }
    },
    [clubId, primaryContactId]
  );

  // A heads-up match the family can't take as secondary: the family's own primary, anyone already a primary contact
  // (primary-XOR-secondary across the platform), or anyone already a secondary for some family (one family at a time).
  // The server enforces all three; this blocks the "use this one instead" so the admin sees why before clicking.
  const duplicateBlockedReason = useCallback(
    (d) => {
      if (d.userID === primaryContactId) return 'Can’t add — this is the primary contact';
      if (d.isPrimaryContact) return 'Can’t add — already a primary contact';
      if (d.isSecondaryContact) return 'Can’t add — already a secondary contact';
      return null;
    },
    [primaryContactId]
  );

  if (!show) return null;

  const addDisabled = loading || !selection || !selection.valid;

  // Add mode is a fixed-size dialog so it never jumps as content changes; the picker's inner panel scrolls.
  // (Edit mode keeps its natural auto height.) See secondary-contact-family-ui-plan.html §14.
  const fixedContentStyle = isEdit
    ? undefined
    : { height: 'min(760px, 92vh)', display: 'flex', flexDirection: 'column' };
  const fixedBodyStyle = isEdit
    ? undefined
    : { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' };
  const fixedFormStyle = isEdit ? undefined : { flex: 1, minHeight: 0 };

  return (
    <>
      {/* No overlay click-to-dismiss — closing is deliberate (the × button). */}
      <div className={styles.modalOverlay}>
        <div className={styles.modalContent} style={fixedContentStyle}>
          <div className={styles.modalHeader}>
            <h2 className={styles.modalTitle}>
              {isEdit ? 'Edit Secondary Contact' : 'Add Secondary Contact'}
            </h2>
            <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
              &times;
            </button>
          </div>

          <div className={styles.modalBody} style={fixedBodyStyle}>
            <div className={styles.form} style={fixedFormStyle}>
              {isEdit ? (
                <>
                  <NameInputBox
                    label="Contact Name"
                    formValues={formValues}
                    errors={errors}
                    handleInputChange={handleInputChange}
                  />

                  <CustomInput
                    label="Email Address"
                    name="email"
                    value={formValues.email}
                    placeholder="contact@example.com"
                    onChange={handleInputChange}
                    error={errors.email}
                    disabled={!canEditEmail}
                  />

                  {/* Club Status before Role — same order as MemberFormModal. Role is the CustomDropdown whose
                      isText help text carries a fixed 73px bottom spacer; keeping it last leaves that space above
                      the footer (as in the member form) instead of opening a gap mid-form before Club Status. */}
                  {canEditRoleAndStatus ? (
                    <CustomSelect
                      label="Club Status"
                      options={participationStatusOptions}
                      value={formValues.participationStatus}
                      name="participationStatus"
                      error={errors.participationStatus}
                      onChange={handleInputChange}
                    />
                  ) : (
                    <LabelText label="Club Status" text={formValues.participationStatus} />
                  )}

                  {canEditRoleAndStatus ? (
                    <CustomDropdown
                      label="Role"
                      name="role"
                      value={formValues.role}
                      onChange={handleDropBoxChange}
                      options={roleOptionsFor(contact?.role)}
                      placeholder=""
                      isText={true}
                    />
                  ) : (
                    <LabelText label="Role" text={formValues.role} />
                  )}
                </>
              ) : (
                <>
                  <p style={{ fontSize: '0.85rem', color: '#4b5563', marginTop: 0 }}>
                    Search for an existing club member to add as the secondary contact. If they’re not in the
                    system yet, add them as a new person.
                  </p>
                  <MemberSearchPicker
                    clubId={clubId}
                    primaryContactId={primaryContactId}
                    excludeUserIds={[primaryContactId]}
                    eligibilityCheck={secondaryEligibility}
                    findDuplicates={findDuplicates}
                    duplicateBlockedReason={duplicateBlockedReason}
                    onChange={setSelection}
                    maxResults={8}
                    createLabel="Add a new secondary contact"
                    actionSlot={
                      <button
                        className={styles.btnPrimary}
                        onClick={handleSubmit}
                        disabled={addDisabled}
                      >
                        {loading ? 'Saving...' : 'Add Contact'}
                      </button>
                    }
                  />
                  <p style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                    Role and club status are set automatically to match the family —{' '}
                    <strong>Member</strong>, status <strong>Active</strong>. An admin can change them after adding.
                  </p>
                </>
              )}
            </div>
          </div>

          {isEdit && (
            <div className={styles.modalFooter}>
              <button className={styles.btnPrimary} onClick={handleSubmit} disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>

      <UserLookupConfirmDialog
        show={showLookupDialog}
        lookupResult={lookupResult}
        purpose="secondary"
        context={{
          primaryContactId,
          primaryContactName,
          typedFirstName: selection?.firstName,
          typedLastName: selection?.lastName,
        }}
        loading={loading}
        onConfirm={handleLookupConfirm}
        onCancel={handleLookupCancel}
      />
    </>
  );
};

SecondaryContactModal.propTypes = {
  show: PropTypes.bool.isRequired,
  contact: PropTypes.object,
  primaryContactId: PropTypes.string.isRequired,
  primaryContactName: PropTypes.string,
  clubId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default SecondaryContactModal;
